import { ModalAlert } from '@/components'
import { ComponentType, ForwardedRef, forwardRef, useCallback } from 'react'
import { RedDotLog, useOpenService, useOpenServiceUrl, useSendEventLog } from '@/hooks'
import { useAccessibleDetailsConditions } from '@/pages/benefit/list/_shared'
import { useModalManager } from '@tmap-web-lib/modal-manager'
import { useSelector } from 'react-redux'
import { selectSettingsIsCarLifeTermsAgreed } from '@/store/settings'
import { formatIntegerKilometers } from '@/utils'
import { BenefitList, BenefitList as Benefit, ReportsResponseData } from '@tmap-web-lib/remote-api-client/frontman'
import { pageIdInfos } from '@/providers'

export interface ItemViewProps {
  data: BenefitList;
  onClick: () => void;
}

interface Props {
  data: Benefit;
  categoryName: string;
  drivingScoreData?: ReportsResponseData;
  redDotLog?: RedDotLog;
  handleTermsCheck?: () => void;
  ItemView: ComponentType<ItemViewProps>;
}

const Item = forwardRef((props: Props, ref: ForwardedRef<HTMLLIElement>) => {
  const {
    data,
    drivingScoreData,
    categoryName,
    redDotLog,
    ItemView,
    handleTermsCheck,
  } = props
  const isCarLifeTermsAgreed = useSelector(selectSettingsIsCarLifeTermsAgreed)

  const modalManager = useModalManager()
  const openService = useOpenService()
  const openServiceUrl = useOpenServiceUrl()
  const conditionsCheck = useAccessibleDetailsConditions({
    score: drivingScoreData?.score ?? 0,
    totalTripDistance: drivingScoreData?.totalMileage ?? 0,
    minimumScore: data.condition.minimumScore || 0,
    minimumTotalMileage: 500000,
  })
  const sendEventLog = useSendEventLog()

  const showDetailCondition = useCallback((score: number, mileage: number, description: string) => {
    const title = `${score}점 이상, ${formatIntegerKilometers(mileage)}km 이상\n운전을 해야 받는 혜택이에요`
    const detail = `${description}\n안전운전하고 꼭 혜택 받아보세요💪`
    modalManager.showModal({
      render: ({ close }) => {
        return (
          <ModalAlert
            title={title}
            detail={detail}
            onOk={() => close()}
          />
        )
      },
    })
  }, [modalManager])

  const handleClick = useCallback(() => {
    // 상위 컴포넌트에 handleTermsCheck props가 없다면 약관동의를 체크하지 않는 것으로 간주하면 된다.
    // 메인에서는 필수약관을 체크하지만 handleTermsCheck props가 없기 때문에 체크하지 않는 것으로 본다.
    if (!isCarLifeTermsAgreed) {
      if (handleTermsCheck) {
        handleTermsCheck()
        return
      }
    }

    const actionId = 'tap.benefit_item'
    const custom = {
      category_id: data.categoryId,
      category_title: categoryName,
      benefit_id: data.benefitId,
      benefit_title: data.benefitTitle,
      reddot: redDotLog,
    }

    // 조건 미달 시 모달 오픈
    if (data.condition.minimumScore) {
      const { minimumScore } = data.condition
      const description = conditionsCheck()
      if (description) {
        sendEventLog({
          actionId: actionId,
          custom: {
            ...custom,
            popup: 'Y',
          },
        })
        showDetailCondition(minimumScore, 500000, description)
        return
      } else {
        sendEventLog({
          actionId: actionId,
          custom: {
            ...custom,
            popup: 'N',
          },
        })
      }
    } else {
      sendEventLog({
        actionId: actionId,
        custom: {
          ...custom,
          popup: 'N',
        },
      })
    }
    const landingUrl = data.option.link?.landingUrl || ''
    if (landingUrl.startsWith('tmap://life')) {
      if (landingUrl === `tmap://life?pageid=${pageIdInfos.benefit_detail.pageId}`) {
        const extra = { seq: data.benefitId }
        openServiceUrl(`${landingUrl}&extra=${encodeURIComponent(JSON.stringify(extra))}`)
      } else {
        openServiceUrl(landingUrl)
      }
    } else {
      openService(landingUrl)
    }
  }, [
    data,
    categoryName,
    isCarLifeTermsAgreed,
    conditionsCheck,
    showDetailCondition,
    redDotLog,
    openServiceUrl,
    openService,
    handleTermsCheck,
    sendEventLog,
  ])

  return (
    <li ref={ref} id={data.benefitId}>
      <ItemView data={data} onClick={handleClick} />
    </li>
  )
})

Item.displayName = 'Item'
export { Item }
